import { Dot } from "@tamagui/lucide-icons";
import { useState } from "react";

import { useStartIdVerification, useUpsertUserProfile } from "@/api/generated";
import {
  BulletItem,
  Button,
  CloseButton,
  Heading,
  Paragraph,
  YStack,
} from "@/components";
import { usePersonaEmbeddedInquiry } from "@/hooks/usePersonaEmbeddedInquiry";

interface IDVerificationSidebarProps {
  onClose: () => void;
}

export default function IDVerificationSidebar({
  onClose,
}: IDVerificationSidebarProps) {
  const [isWaiting, setIsWaiting] = useState(false);

  const { start, isStarting, error } = usePersonaEmbeddedInquiry({
    onComplete: ({ inquiryId, status }) => {
      console.log("Persona complete", inquiryId, status);
      onClose();
    },
    onCancel: ({ inquiryId }) => {
      setIsWaiting(false);
      console.log("Persona canceled", inquiryId);
    },
    onPersonaError: (err) => {
      setIsWaiting(false);
      console.error("Persona error", err);
    },
    onResumeError: (err) => {
      setIsWaiting(false);
      console.error("Failed to start Persona verification", err);
    },
  });

  const updateUserProfileMutation = useUpsertUserProfile();

  const startVerificationMutation = useStartIdVerification({
    mutation: {
      onSuccess: () => {
        console.log("Start verification successful, waiting 5 seconds...");
        setIsWaiting(true);
        setTimeout(() => {
          // waiting for webhook response from Persona
          start();
        }, 5000);
      },
      onError: (err) => {
        console.error("Failed to start verification", err);
      },
    },
  });

  const handleStart = () => {
    if (
      startVerificationMutation.isPending ||
      isWaiting ||
      isStarting ||
      updateUserProfileMutation.isPending
    )
      return;

    // First upsert user profile with fake address
    updateUserProfileMutation.mutate(
      {
        data: {
          home_address: {
            street_address: "123 Main Street",
            city: "fail", // this forces a failure
            state: "CA",
            zip_code: "94102",
          },
        },
      },
      {
        onSuccess: () => {
          console.log("User profile updated with fake address");
          // Now start verification
          startVerificationMutation.mutate();
        },
        onError: (err) => {
          console.error("Failed to update user profile", err);
        },
      },
    );
  };

  const isOpeningPersona =
    startVerificationMutation.isPending || isWaiting || isStarting;

  return (
    <YStack
      flex={1}
      padding="$300"
      borderRadius="$150"
      backgroundColor="$fillModal"
    >
      <CloseButton onClose={onClose} dataTrackingId="id-verification-close" />

      <YStack gap="$200" marginVertical="$200">
        <Heading size="$xl">Test Gov ID Verification</Heading>
        <Paragraph color="$textSecondary">
          Welcome to our sandbox for testing the Gov ID verification flow!
          Clicking &quot;start&quot; will do the following actions:
        </Paragraph>
      </YStack>

      <YStack gap="$200">
        <BulletItem
          key="sidebar-nudge-connect-securely"
          itemText="Start a new Persona Transaction"
          icon={<Dot size="$300" />}
          color="$textSecondary"
          size="$sm"
        />
        <BulletItem
          key="sidebar-nudge-access-care-plans"
          itemText="Force fail the Database check"
          icon={<Dot size="$300" />}
          color="$textSecondary"
          size="$sm"
        />
        <BulletItem
          key="sidebar-nudge-wait-for-persona"
          itemText="Wait for a response from Persona"
          icon={<Dot size="$300" />}
          color="$textSecondary"
          size="$sm"
        />
        <BulletItem
          key="sidebar-nudge-kick-off"
          itemText="Kick off the embedded Gov ID Check"
          icon={<Dot size="$300" />}
          color="$textSecondary"
          size="$sm"
        />
      </YStack>

      <YStack gap="$100" marginTop="auto">
        <Paragraph marginBottom="$200" size="$sm" color="$textSecondary">
          Please note you can only do this once per user right now.
        </Paragraph>

        {error ? (
          <Paragraph size="$sm" color="$textSecondary">
            {error}
          </Paragraph>
        ) : null}
      </YStack>

      <Button onPress={handleStart} disabled={isOpeningPersona}>
        {startVerificationMutation.isPending
          ? "Starting verification…"
          : isWaiting
            ? "Waiting (5s)…"
            : "Start"}
      </Button>
    </YStack>
  );
}
